import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export const CAN_EDIT = ["admin", "owner", "admin_kantor"];
export const CAN_DELETE = ["admin", "owner"];

const initial = { name: "", address: "", contact_person: "", phone: "", notes: "" };

export default function CustomerManagementPage() {
  const { user } = useAuth();
  const canEdit = CAN_EDIT.includes(user?.role);
  const canDelete = CAN_DELETE.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [q, setQ] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch(() => {});
  useAutoRefresh(load);

  const openNew = () => { setForm(initial); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ name: r.name, address: r.address || "", contact_person: r.contact_person || "", phone: r.phone || "", notes: r.notes || "" });
    setEditingId(r.id); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await api.patch(`/customers/${editingId}`, form);
        toast.success("Customer diperbarui");
      } else {
        await api.post("/customers", form);
        toast.success(`Customer ${form.name} tersimpan`);
      }
      setForm(initial); setEditingId(null); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus customer ${row.name}?`)) return;
    try {
      await api.delete(`/customers/${row.id}`);
      toast.success("Customer dihapus"); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = rows.filter((r) => !q ||
    `${r.name} ${r.address} ${r.contact_person}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AppShell
      title="Data Customer"
      subtitle="Master customer — tersimpan otomatis saat DO baru dibuat"
      actions={
        <Button onClick={openNew} data-testid="cust-new-btn" className="bg-sky-600 hover:bg-sky-700 h-10 gap-2">
          <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Customer Baru</span>
        </Button>
      }
    >
      <div className="flex items-center gap-3 mb-5">
        <Input data-testid="cust-search" placeholder="Cari nama, alamat, PIC…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm h-10" />
        <span className="ml-auto text-xs font-mono text-slate-500">{filtered.length} customer</span>
      </div>

      <DataTable
        testid="customer-table"
        rows={filtered}
        columns={[
          { key: "name", label: "Nama", render: (r) => <span className="font-semibold text-slate-800">{r.name}</span> },
          { key: "address", label: "Alamat", render: (r) => <span className="text-slate-600 text-[12px]">{r.address || "—"}</span> },
          { key: "contact_person", label: "PIC" },
          { key: "phone", label: "HP", render: (r) => <span className="font-mono text-[12px]">{r.phone || "—"}</span> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-1">
              {canEdit && (
                <button onClick={() => openEdit(r)} data-testid={`cust-edit-${r.name}`}
                  className="text-sky-700 hover:text-sky-900 p-1.5 hover:bg-sky-50 rounded-md" title="Edit"><PencilSimple size={15} weight="duotone" /></button>
              )}
              {canDelete && (
                <button onClick={() => remove(r)} data-testid={`cust-del-${r.name}`}
                  className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-md" title="Hapus"><Trash size={15} weight="duotone" /></button>
              )}
            </div>
          )},
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editingId ? "Edit Customer" : "Customer Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="cust-form" className="space-y-3 mt-2">
            <Field label="Nama Customer" required><Input data-testid="cust-name" value={form.name} onChange={set("name")} required autoFocus /></Field>
            <Field label="Alamat"><Textarea data-testid="cust-address" rows={2} value={form.address} onChange={set("address")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PIC / Contact"><Input data-testid="cust-pic" value={form.contact_person} onChange={set("contact_person")} /></Field>
              <Field label="No. HP"><Input data-testid="cust-phone" value={form.phone} onChange={set("phone")} className="font-mono" /></Field>
            </div>
            <Field label="Catatan"><Textarea data-testid="cust-notes" rows={2} value={form.notes} onChange={set("notes")} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="cust-submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white">
                {submitting ? "Menyimpan…" : editingId ? "Perbarui" : "Simpan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
